/**
 * Assessments Hooks
 *
 * TanStack Query hooks for assessment operations including:
 * - List assessments with filters
 * - Create manual assessments
 * - Get assessment details
 * - Override scores
 * - Download PDF report
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';
import type {
  AssessmentListItem,
  AssessmentDetail,
  AssessmentListQuery,
  CreateManualAssessment,
  CreateAssessmentV2,
  OverrideAssessment,
  RubricOption,
  SectionQuestion,
  SubmitFeedback,
} from '@guild/shared';

// Query keys
export const assessmentKeys = {
  all: ['assessments'] as const,
  lists: () => [...assessmentKeys.all, 'list'] as const,
  list: (filters: AssessmentListQuery & { page?: number; limit?: number }) =>
    [...assessmentKeys.lists(), filters] as const,
  details: () => [...assessmentKeys.all, 'detail'] as const,
  detail: (id: string) => [...assessmentKeys.details(), id] as const,
  evidenceClips: (id: string) => [...assessmentKeys.all, 'evidence-clips', id] as const,
  feedback: (id: string) => [...assessmentKeys.all, 'feedback', id] as const,
};

// Response types
interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ============================================
// LIST QUERIES
// ============================================

/**
 * Fetch assessments list with pagination and filters
 */
export function useAssessments(
  options?: AssessmentListQuery & { page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }
) {
  const api = useApi();

  return useQuery({
    queryKey: assessmentKeys.list(options ?? {}),
    queryFn: async () => {
      const params = new URLSearchParams();

      if (options?.operatorId) params.set('operatorId', options.operatorId);
      if (options?.sopId) params.set('sopId', options.sopId);
      if (options?.departmentId) params.set('departmentId', options.departmentId);
      if (options?.assessmentType) params.set('assessmentType', options.assessmentType);
      if (options?.minScore !== undefined) params.set('minScore', String(options.minScore));
      if (options?.maxScore !== undefined) params.set('maxScore', String(options.maxScore));
      if (options?.startDate) params.set('startDate', options.startDate.toISOString());
      if (options?.endDate) params.set('endDate', options.endDate.toISOString());
      if (options?.page) params.set('page', String(options.page));
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.sortBy) params.set('sortBy', options.sortBy);
      if (options?.sortOrder) params.set('sortOrder', options.sortOrder);

      const queryString = params.toString();
      const endpoint = queryString ? `/api/assessments?${queryString}` : '/api/assessments';

      return api.get<PaginatedResponse<AssessmentListItem>>(endpoint);
    },
  });
}

/**
 * Fetch single assessment details
 */
export function useAssessment(id: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: assessmentKeys.detail(id ?? ''),
    queryFn: () => api.get<AssessmentDetail>(`/api/assessments/${id}`),
    enabled: !!id,
  });
}

// ============================================
// MUTATIONS
// ============================================

/**
 * Create manual assessment
 */
export function useCreateAssessment() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateManualAssessment) =>
      api.post<AssessmentDetail>('/api/assessments', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assessmentKeys.all });
      // Also invalidate skill matrix and dashboard
      queryClient.invalidateQueries({ queryKey: ['skill-matrix'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

/**
 * Update assessment (manager notes only)
 */
export function useUpdateAssessment() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { managerNotes?: string } }) =>
      api.put<AssessmentDetail>(`/api/assessments/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: assessmentKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: assessmentKeys.lists() });
    },
  });
}

/**
 * Override assessment scores
 */
export function useOverrideAssessment() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: OverrideAssessment }) =>
      api.post<AssessmentDetail>(`/api/assessments/${id}/override`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: assessmentKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: assessmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['skill-matrix'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

// ============================================
// PHASE 12.2: CREATE FROM VIDEO (open-ended manual trigger)
// ============================================

export interface CreateFromVideoInput {
  startTime: string;
  endTime: string;
  cameraId: string;
  operatorId?: string;
  sopId?: string;
  videoClipId?: string;
  notes?: string;
  kpiDefinitionId?: string; // Phase 24.5: Optional KPI analysis
  sourceNumber?: number; // Phase 35.5: 1-based lens for multi-sensor cameras; absent = lens 1
  // Phase 36.4 v3: Job (section) allowlist for this request. Absent = whole SOP
  // (unchanged behaviour); the API rejects an empty array and any scope sent
  // without a sopId.
  scopeSectionIds?: string[];
}

interface FromVideoResponse {
  triggerId: string;
  jobId: string;
  message: string;
}

/**
 * Create automated assessment from video (Phase 12.2)
 * Only startTime, endTime, and cameraId are required.
 */
export function useCreateFromVideo() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateFromVideoInput) =>
      api.post<FromVideoResponse>('/api/assessments/from-video', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assessmentKeys.all });
    },
  });
}

/**
 * Fetch trigger details (Phase 12.2)
 */
export function useTriggerDetail(triggerId: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: ['triggers', 'detail', triggerId] as const,
    queryFn: () => api.get<Record<string, unknown>>(`/api/assessments/triggers/${triggerId}`),
    enabled: !!triggerId,
    refetchInterval: (query) => {
      // Poll every 5s while trigger is processing
      const data = query.state.data as Record<string, unknown> | undefined;
      const status = data?.status;
      if (
        status === 'pending' ||
        status === 'pulling_video' ||
        status === 'analyzing' ||
        status === 'pending_review' ||
        status === 'pending_section_review'
      ) {
        return 5000;
      }
      // Phase 41A (S14): every 10s while a completed trigger's own KPI has
      // neither a saved result nor a recorded run outcome yet. The API sets
      // `kpiRunning` only within 60 minutes of the trigger completing; the
      // previous API omits it.
      return data?.kpiRunning === true ? 10000 : false;
    },
  });
}

// ============================================
// PDF DOWNLOAD
// ============================================

/**
 * Download assessment PDF
 */
export function useDownloadAssessmentPdf() {
  const api = useApi();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.getBlob(`/api/assessments/${id}/pdf`);
      return { id, blob: response };
    },
    onSuccess: ({ id, blob }) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `assessment-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },
  });
}

// ============================================
// PHASE 8.1: V2 ASSESSMENT HOOKS
// ============================================

/**
 * Fetch rubric options (6-point scale)
 */
export function useRubricOptions() {
  const api = useApi();

  return useQuery({
    queryKey: ['rubric-options'],
    queryFn: () => api.get<RubricOption[]>('/api/assessments/rubric-options'),
    staleTime: 1000 * 60 * 60, // Cache for 1 hour - these rarely change
  });
}

/**
 * Fetch questions for a specific SOP section (job)
 */
export function useSectionQuestions(sopId: string | null, sectionId: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: ['section-questions', sopId, sectionId],
    queryFn: () =>
      api.get<{
        section: { id: string; sectionNumber: number; title: string };
        questions: SectionQuestion[];
      }>(`/api/sops/${sopId}/sections/${sectionId}/questions`),
    enabled: !!sopId && !!sectionId,
  });
}

/**
 * Create v2 assessment (question-based)
 */
export function useCreateAssessmentV2() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAssessmentV2) =>
      api.post<AssessmentDetail>('/api/assessments/v2', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assessmentKeys.all });
      queryClient.invalidateQueries({ queryKey: ['skill-matrix'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

// ============================================
// PHASE 17: EVIDENCE CLIPS & FEEDBACK
// ============================================

/**
 * Evidence clip with associated step details
 */
export interface EvidenceClip {
  id: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  presignedUrl: string | null;
  startTimestamp: number | null;
  endTimestamp: number | null;
  durationSeconds: number | null;
  errorMessage: string | null;
  createdAt: string;
  step: {
    id: string;
    stepNumber: number;
    description: string;
    isSafetyCritical: boolean;
    passed: boolean;
    score: number;
    feedback: string | null;
    timestamp: number | null;
  } | null;
}

/**
 * Feedback entry for an assessment
 */
export interface AssessmentFeedbackEntry {
  id: string;
  assessmentId: string;
  stepId: string | null;
  questionResponseId: string | null;
  agreesWithAi: boolean | null;
  correctedScore: number | null;
  rationale: string | null;
  overallRating: number | null;
  overallComments: string | null;
  submittedBy: string | null;
  createdAt: string;
  submittedByUser: { id: string; fullName: string } | null;
}

/**
 * Fetch evidence clips for an assessment (Phase 17)
 */
export function useEvidenceClips(assessmentId: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: assessmentKeys.evidenceClips(assessmentId ?? ''),
    queryFn: () =>
      api.get<{ clips: EvidenceClip[] }>(`/api/assessments/${assessmentId}/evidence-clips`),
    enabled: !!assessmentId,
  });
}

/**
 * Fetch feedback entries for an assessment (Phase 17)
 */
export function useAssessmentFeedback(assessmentId: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: assessmentKeys.feedback(assessmentId ?? ''),
    queryFn: () =>
      api.get<{ feedback: AssessmentFeedbackEntry[] }>(`/api/assessments/${assessmentId}/feedback`),
    enabled: !!assessmentId,
  });
}

/**
 * Submit per-step + overall feedback for an assessment (Phase 17)
 */
export function useSubmitFeedback() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ assessmentId, data }: { assessmentId: string; data: SubmitFeedback }) =>
      api.post<{ stepFeedback: boolean; overallFeedback: boolean }>(
        `/api/assessments/${assessmentId}/feedback`,
        data
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: assessmentKeys.feedback(variables.assessmentId),
      });
      queryClient.invalidateQueries({
        queryKey: assessmentKeys.evidenceClips(variables.assessmentId),
      });
    },
  });
}
